import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface TimePickerProps {
  value: string; // 24-hour format "HH:MM"
  onChange: (value: string) => void;
}

const to12Hour = (time24: string): { hour: string; minute: string; period: string } => {
  if (!time24) return { hour: '12', minute: '00', period: 'AM' };
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return { hour: String(hour12), minute: String(m).padStart(2, '0'), period };
};

const to24Hour = (hour: string, minute: string, period: string): string => {
  let h = parseInt(hour);
  if (period === 'AM' && h === 12) h = 0;
  if (period === 'PM' && h !== 12) h += 12;
  return `${String(h).padStart(2, '0')}:${minute}`;
};

export const TimePicker = ({ value, onChange }: TimePickerProps) => {
  const { hour, minute, period } = to12Hour(value);

  const update = (newHour?: string, newMinute?: string, newPeriod?: string) => {
    onChange(to24Hour(newHour ?? hour, newMinute ?? minute, newPeriod ?? period));
  };

  return (
    <div className="flex items-center gap-1.5">
      <Select value={hour} onValueChange={(v) => update(v, undefined, undefined)}>
        <SelectTrigger className="w-[70px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Array.from({ length: 12 }, (_, i) => String(i + 1)).map(h => (
            <SelectItem key={h} value={h}>{h}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-muted-foreground font-bold">:</span>
      <Select value={minute} onValueChange={(v) => update(undefined, v, undefined)}>
        <SelectTrigger className="w-[70px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {['00', '15', '30', '45'].map(m => (
            <SelectItem key={m} value={m}>{m}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={period} onValueChange={(v) => update(undefined, undefined, v)}>
        <SelectTrigger className="w-[75px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="AM">AM</SelectItem>
          <SelectItem value="PM">PM</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};
